import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const ProductFormModal = ({ product, tenantId, userId, onClose, onSaved }) => {
  const [formData, setFormData] = useState({
    category_id: product?.category_id || '',
    sku: product?.sku || '',
    barcode: product?.barcode || '',
    name: product?.name || '',
    description: product?.description || '',
    image_url: product?.image_url || '',
    base_price: product?.base_price || '',
    tax_rate: product?.tax_rate || 0,
    cost_price: product?.cost_price || '',
    status: product?.status || 'active',
    track_inventory: product?.track_inventory ?? true,
    stock_quantity: product?.stock_quantity || 0,
    low_stock_threshold: product?.low_stock_threshold || 10,
    reorder_quantity: product?.reorder_quantity || 50,
    supplier_name: product?.supplier_name || '',
    supplier_contact: product?.supplier_contact || ''
  });
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const { data, error } = await supabase
          ?.from('pos_categories')
          ?.select('*')
          ?.eq('tenant_id', tenantId)
          ?.eq('is_active', true)
          ?.order('name');

        if (error) throw error;
        setCategories(data || []);
      } catch (error) {
        console.error('Load categories error:', error);
      }
    };

    if (tenantId) {
      loadCategories();
    }
  }, [tenantId]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData?.name?.trim() || !formData?.sku?.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const productData = {
        tenant_id: tenantId,
        name: formData?.name?.trim(),
        sku: formData?.sku?.trim(),
        description: formData?.description?.trim() || null,
        category_id: formData?.category_id || null,
        price: parseFloat(formData?.base_price) || 0,
        cost: parseFloat(formData?.cost_price) || 0,
        stock_quantity: parseInt(formData?.stock_quantity) || 0,
        low_stock_threshold: parseInt(formData?.low_stock_threshold) || 10,
        barcode: formData?.barcode?.trim() || null,
        image_url: formData?.image_url?.trim() || null,
        track_inventory: formData?.track_inventory,
        is_active: formData?.status === 'active',
        created_by: userId
      };

      let error;
      if (product) {
        // Update existing product
        const result = await supabase
          ?.from('pos_products')
          ?.update(productData)
          ?.eq('id', product?.id);
        error = result?.error;
      } else {
        // Create new product
        const result = await supabase
          ?.from('pos_products')
          ?.insert([productData]);
        error = result?.error;
      }

      if (error) throw error;

      onSaved?.();
    } catch (error) {
      console.error('Save product error:', error);
      alert('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto pt-24">
      <div className="bg-card rounded-xl shadow-xl max-w-md w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-base font-bold text-foreground">
            {product ? 'Edit Product' : 'Add New Product'}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4">
          <div className="grid grid-cols-1 gap-3">
            {/* Basic Info */}
            <div>
              <h3 className="text-base font-semibold text-foreground mb-3">Basic Information</h3>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Product Name *
              </label>
              <input
                type="text"
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                required
                className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                SKU *
              </label>
              <input
                type="text"
                value={formData?.sku}
                onChange={(e) => setFormData({ ...formData, sku: e?.target?.value })}
                required
                className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Barcode
              </label>
              <input
                type="text"
                value={formData?.barcode}
                onChange={(e) => setFormData({ ...formData, barcode: e?.target?.value })}
                className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Category
              </label>
              <select
                value={formData?.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e?.target?.value })}
                className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                <option value="">No Category</option>
                {categories?.map((cat) => (
                  <option key={cat?.id} value={cat?.id}>{cat?.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <select
                value={formData?.status}
                onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
                className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="out_of_stock">Out of Stock</option>
                <option value="discontinued">Discontinued</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                rows={3}
                className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground resize-none"
              />
            </div>

            {/* Pricing */}
            <div className="mt-3">
              <h3 className="text-base font-semibold text-foreground mb-3">Pricing</h3>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Base Price *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData?.base_price}
                  onChange={(e) => setFormData({ ...formData, base_price: e?.target?.value })}
                  required
                  className="w-full pl-8 pr-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Tax Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData?.tax_rate}
                onChange={(e) => setFormData({ ...formData, tax_rate: e?.target?.value })}
                className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Cost Price
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData?.cost_price}
                  onChange={(e) => setFormData({ ...formData, cost_price: e?.target?.value })}
                  className="w-full pl-8 pr-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                />
              </div>
            </div>

            {/* Inventory */}
            <div className="mt-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-foreground">Inventory Management</h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.track_inventory}
                    onChange={(e) => setFormData({ ...formData, track_inventory: e?.target?.checked })}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">Track Inventory</span>
                </label>
              </div>
            </div>

            {formData?.track_inventory && (
              <>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Current Stock
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData?.stock_quantity}
                    onChange={(e) => setFormData({ ...formData, stock_quantity: e?.target?.value })}
                    className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Low Stock Alert
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData?.low_stock_threshold}
                    onChange={(e) => setFormData({ ...formData, low_stock_threshold: e?.target?.value })}
                    className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Reorder Quantity
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData?.reorder_quantity}
                    onChange={(e) => setFormData({ ...formData, reorder_quantity: e?.target?.value })}
                    className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  />
                </div>
              </>
            )}

            {/* Supplier */}
            <div className="mt-3">
              <h3 className="text-base font-semibold text-foreground mb-3">Supplier Information</h3>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Supplier Name
              </label>
              <input
                type="text"
                value={formData?.supplier_name}
                onChange={(e) => setFormData({ ...formData, supplier_name: e?.target?.value })}
                className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Supplier Contact
              </label>
              <input
                type="text"
                value={formData?.supplier_contact}
                onChange={(e) => setFormData({ ...formData, supplier_contact: e?.target?.value })}
                className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-5 pt-5 border-t border-border">
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} className="mr-2" />
                  {product ? 'Update Product' : 'Create Product'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductFormModal;